import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Tabs, 
  Tab, 
  Container, 
  Typography, 
  CircularProgress, 
  Alert, 
  Paper, 
  Chip, 
  LinearProgress, 
  Button, 
  IconButton, 
  Grid,
  Divider,
  Rating,
  TextField,
  FormControlLabel,
  Checkbox,
  Avatar,
  useTheme
} from '@mui/material';
import { 
  DateRange as CalendarIcon, 
  CheckCircle as CheckCircleIcon, 
  Schedule as ScheduleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Message as MessageIcon,
  Speed as SpeedIcon,
  LocalParking as ParkingIcon,
  EmojiEvents as TrophyIcon,
  MenuBook as BookIcon,
  Lock as LockIcon,
  Share as ShareIcon,
  Map as MapIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { api } from '../../services/api';
import { useConfirm, useToast } from '../../contexts/ConfirmContext';
import ReferralAndReviews from '../../components/ReferralAndReviews';
import TestRoutesTab from './TestRoutesTab';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`student-tabpanel-${index}`}
      aria-labelledby={`student-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `student-tab-${index}`,
    'aria-controls': `student-tabpanel-${index}`,
  };
}
interface Topic {
  id: string;
  topicName: string;
  covered: boolean;
  rating: number;
}

interface Lesson {
  id: string;
  lessonDate: string;
  duration: number;
  instructorName: string;
  notes?: string;
  completed?: boolean;
  isApproved?: boolean;
  topicId?: number;
  topicName?: string;
  topicCode?: string;
  topics?: Topic[];
}

const getImageUrl = (path: string | null) => {
  return api.getImageUrl(path);
};

const BADGES_METADATA = [
  {
    type: 'FirstGear',
    title: 'First Gear',
    requirement: 'Complete your first lesson',
    icon: <SpeedIcon sx={{ fontSize: 40 }} />,
    color: '#3f51b5',
    gradient: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)'
  },
  {
    type: 'ParkingMaster',
    title: 'Parking Master',
    requirement: 'Cover 5 DVSA syllabus topics',
    icon: <ParkingIcon sx={{ fontSize: 40 }} />,
    color: '#ff9800',
    gradient: 'linear-gradient(135deg, #ff9800 0%, #ffc107 100%)'
  },
  {
    type: 'SyllabusHero',
    title: 'Syllabus Hero',
    requirement: 'Cover 15 DVSA syllabus topics',
    icon: <TrophyIcon sx={{ fontSize: 40 }} />,
    color: '#9c27b0',
    gradient: 'linear-gradient(135deg, #9c27b0 0%, #ea1e63 100%)'
  },
  {
    type: 'TheoryExpert',
    title: 'Theory Expert',
    requirement: 'Pass a mock theory exam',
    icon: <BookIcon sx={{ fontSize: 40 }} />,
    color: '#4caf50',
    gradient: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)'
  }
];

const StudentDashboard: React.FC = () => {
  const theme = useTheme();
  const [value, setValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [upcomingLessons, setUpcomingLessons] = useState<Lesson[]>([]);
  const [lessonHistory, setLessonHistory] = useState<Lesson[]>([]);
  const [progress, setProgress] = useState<any | null>(null);
  const [badges, setBadges] = useState<any[]>([]);
  const navigate = useNavigate();
  const confirm = useConfirm();
  const showToast = useToast();

  // Availability browser states
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [availableSlots, setAvailableSlots] = useState<any[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Review states
  const [reviewRating, setReviewRating] = useState<number>(5);
  const [reviewComment, setReviewComment] = useState<string>('');
  const [isPassedPracticalTest, setIsPassedPracticalTest] = useState<boolean>(false);
  const [submittingReview, setSubmittingReview] = useState<boolean>(false);
  const [hasExistingReview, setHasExistingReview] = useState<boolean>(false);

  const [quotes, setQuotes] = useState<any[]>([]);

  const fetchAvailableSlots = async (date: Date) => {
    try {
      setLoadingSlots(true);
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await api.get(`/students/available-slots?date=${dateStr}`);
      setAvailableSlots(response.data);
    } catch (err) {
      console.error("Error loading availabilities:", err);
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    if (value === 2) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate, value]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch upcoming lessons
      const upcomingResponse = await api.get('/students/lessons?upcoming=true');
      setUpcomingLessons(upcomingResponse.data);
      
      // Fetch lesson history
      const historyResponse = await api.get('/students/lessons');
      setLessonHistory(historyResponse.data);

      // Fetch lessons progress
      const progressResponse = await api.getStudentLessonsProgress();
      const progressData = progressResponse.data;
      setProgress(progressData);

      // Fetch existing review if assigned instructor exists
      if (progressData && progressData.instructorId) {
        try {
          const reviewResponse = await api.getMyReviewForInstructor(progressData.instructorId);
          if (reviewResponse.data) {
            setReviewRating(reviewResponse.data.rating || 5);
            setReviewComment(reviewResponse.data.comment || '');
            setIsPassedPracticalTest(reviewResponse.data.isPassedPracticalTest || false);
            setHasExistingReview(true);
          }
        } catch (rErr: any) {
          // 404 means no review exists yet, which is expected
          if (rErr.response?.status !== 404) {
            console.error("Error fetching existing review:", rErr);
          }
          setHasExistingReview(false);
        }
      }
      
      // Fetch quote requests
      try {
        const quotesResponse = await api.get('/Quotes/my-requests');
        setQuotes(quotesResponse.data || []);
      } catch (qErr) {
        console.error("Error fetching my quotes:", qErr);
      }
      
      // Fetch earned badges
      try {
        const badgesResponse = await api.getMyBadges();
        setBadges(badgesResponse.data || []);
      } catch (bErr) {
        console.error("Error fetching badges:", bErr);
      }
      
      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred while loading data');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCancelLesson = async (lessonId: string) => {
    const lesson = upcomingLessons.find(l => l.id.toString() === lessonId.toString());
    let confirmMessage = 'Are you sure you want to cancel this lesson reservation request?';
    
    if (lesson && lesson.isApproved) {
      const lessonDate = parseISO(lesson.lessonDate);
      const now = new Date();
      const diffHours = (lessonDate.getTime() - now.getTime()) / (1000 * 60 * 60);
      if (diffHours < 24) {
        confirmMessage = '⚠️ LATE CANCELLATION WARNING: This lesson is scheduled to start in less than 24 hours. Cancelling now will incur a late cancellation fee equal to 100% of the lesson price. Are you sure you want to proceed?';
      }
    }

    const isConfirmed = await confirm({
      title: 'Cancel Reservation',
      message: confirmMessage,
      severity: 'warning'
    });
    if (!isConfirmed) return;
    
    try {
      setLoading(true);
      await api.cancelLesson(Number(lessonId));
      showToast('Reservation cancelled successfully.', 'success');
      await fetchData();
      if (value === 2) {
        await fetchAvailableSlots(selectedDate);
      }
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to cancel reservation.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getLessonsForSelectedDate = () => {
    return upcomingLessons.filter(lesson => {
      const lessonDate = parseISO(lesson.lessonDate);
      return format(lessonDate, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
    });
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleSelectSlot = (slot: any) => {
    navigate(`/student/book-lesson?date=${slot.startTime}`);
  };

  const handleSubmitReview = async () => {
    if (!progress?.instructorId) {
      showToast('No assigned instructor found to review.', 'error');
      return;
    }
    try {
      setSubmittingReview(true);
      await api.createReview({
        instructorId: progress.instructorId,
        rating: reviewRating,
        comment: reviewComment,
        isPassedPracticalTest: isPassedPracticalTest
      });
      showToast('Thank you! Your review has been submitted successfully.', 'success');
      await fetchData();
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to submit review.', 'error');
    } finally {
      setSubmittingReview(false);
    }
  };

  const getTheoryTestExpiryCountdown = () => {
    if (!progress?.theoryTestPassDate) return null;
    const passDate = new Date(progress.theoryTestPassDate);
    const expiryDate = new Date(passDate);
    expiryDate.setFullYear(passDate.getFullYear() + 2); // Valid for 2 years
    const diffTime = expiryDate.getTime() - new Date().getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return {
      expiryDate,
      daysLeft: diffDays,
      isExpired: diffDays <= 0
    };
  };

  const theoryCountdown = getTheoryTestExpiryCountdown();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
          Student Dashboard
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate('/search')}
            sx={{ fontWeight: 'bold', borderRadius: 2 }}
          >
            Search Instructors Directory
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => navigate('/student/theory-test')}
            sx={{ fontWeight: 'bold', borderRadius: 2 }}
          >
            Practice Mock Theory Test
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => navigate('/student/hazard-test')}
            sx={{ fontWeight: 'bold', borderRadius: 2 }}
          >
            Practice Hazard Perception
          </Button>
        </Box>
      </Box>

      {/* License Upload / Approval / Theory Exam Warnings */}
      {progress && (
        <Box sx={{ mb: 3 }}>
          {/* Missing Instructor Alert */}
          {!progress.instructorId && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }} action={
              <Button color="inherit" size="small" onClick={() => navigate('/search')}>
                Find Instructor
              </Button>
            }>
              You have not chosen a driving instructor yet! Search the instructor directory to find one and send a request.
            </Alert>
          )}

          {/* Rejection Alert */}
          {progress.instructorId && progress.licenseStatus === 'Rejected' && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} action={
              <Button color="inherit" size="small" onClick={() => navigate('/student/profile')}>
                Update License
              </Button>
            }>
              Your driving license has been rejected by your instructor! Reason: <strong>{progress.licenseRejectionReason || 'Not specified'}</strong>. Please upload new license images in your profile to book a lesson.
            </Alert>
          )}

          {/* Missing License Alert */}
          {progress.instructorId && (progress.licenseStatus === 'None' || !progress.licenseStatus) && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }} action={
              <Button color="inherit" size="small" onClick={() => navigate('/student/profile')}>
                Upload License
              </Button>
            }>
              To book a lesson, you must first upload the front and back images of your driving license.
            </Alert>
          )}

          {/* Pending Approval Alert */}
          {progress.instructorId && progress.licenseStatus === 'Pending' && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              Your driving license files have been uploaded successfully. Awaiting verification and approval from your instructor.
            </Alert>
          )}

          {/* Theory Expiry Alert */}
          {theoryCountdown && (
            <>
              {theoryCountdown.isExpired ? (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  Your theory test has expired (2 years)! You may need to retake the theory test to take the practical driving test (Expiry date: {theoryCountdown.expiryDate.toLocaleDateString('en-GB')}).
                </Alert>
              ) : theoryCountdown.daysLeft <= 90 ? (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  <strong>Critical Warning:</strong> There are only <strong>{theoryCountdown.daysLeft} days</strong> left before your theory test expires! You must pass the practical driving test before this date. Expiry date: {theoryCountdown.expiryDate.toLocaleDateString('en-GB')}.
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                  Theory test validity: Your deadline to complete the practical test is <strong>{theoryCountdown.expiryDate.toLocaleDateString('en-GB')}</strong> ({theoryCountdown.daysLeft} days remaining).
                </Alert>
              )}
            </>
          )}
        </Box>
      )}

      {progress && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, md: progress.instructorId ? 7 : 12 }}>
            <Paper 
              sx={{ 
                p: 3, 
                borderRadius: 3, 
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
                color: '#fff',
                boxShadow: '0 8px 24px rgba(30, 60, 114, 0.15)',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 3,
                height: '100%',
                boxSizing: 'border-box'
              }}
            >
              <Box sx={{ width: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  🇬🇧 UK DVSA Syllabus Progress
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85, mb: 2 }}>
                  DVSA syllabus topics completed out of the 19 core topics defined for driving test preparation.
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box sx={{ width: '100%', mr: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={((progress.coveredDvsaTopicsCount || 0) / 19) * 100} 
                      sx={{ 
                        height: 10, 
                        borderRadius: 5, 
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: '#4caf50'
                        }
                      }} 
                    />
                  </Box>
                  <Box sx={{ minWidth: 35 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {Math.round(((progress.coveredDvsaTopicsCount || 0) / 19) * 100)}%
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderRadius: 3,
                  p: 2,
                  minWidth: 150,
                  textAlign: 'center'
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                  {progress.coveredDvsaTopicsCount || 0} / 19
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 'medium' }}>
                  Topics Completed
                </Typography>
              </Box>
            </Paper>
          </Grid>
          
          {progress.instructorId && (
            <Grid size={{ xs: 12, md: 5 }}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: '1px solid #e0e0e0',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                  backgroundColor: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2.5,
                  height: '100%',
                  boxSizing: 'border-box'
                }}
              >
                {progress.instructorLogoUrl ? (
                  <Avatar
                    variant="rounded"
                    src={getImageUrl(progress.instructorLogoUrl)}
                    sx={{ width: 80, height: 80, bgcolor: '#fff', border: '1px solid #eef2f6', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', p: 0.5 }}
                  />
                ) : (
                  <Avatar
                    variant="rounded"
                    sx={{ width: 80, height: 80, bgcolor: '#1a237e', color: '#fff', fontSize: '2rem', fontWeight: 'bold' }}
                  >
                    {progress.instructorName ? progress.instructorName[0] : 'I'}
                  </Avatar>
                )}
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1 }}>
                    Assigned Instructor
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e', mt: 0.5 }}>
                    {progress.instructorName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span>📧</span> {progress.instructorEmail}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span>📞</span> {progress.instructorPhone || 'No phone number'}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* Badges & Milestones Showcase */}
      {progress && (
        <Paper
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#1a237e', display: 'flex', alignItems: 'center', gap: 1 }}>
            🏆 My Badges & Milestones
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Earn premium achievement badges as you progress through your driving curriculum and pass your test components.
          </Typography>

          <Grid container spacing={2}>
            {BADGES_METADATA.map((badgeMeta) => {
              const earnedBadge = badges.find((b) => b.badgeType === badgeMeta.type);
              const isEarned = !!earnedBadge;

              return (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={badgeMeta.type}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: isEarned ? 'transparent' : 'divider',
                      background: isEarned 
                        ? badgeMeta.gradient 
                        : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fafafa',
                      color: isEarned ? '#fff' : 'text.secondary',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      minHeight: 180,
                      position: 'relative',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      boxShadow: isEarned ? '0 4px 14px rgba(0,0,0,0.1)' : 'none',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: isEarned ? '0 6px 18px rgba(0,0,0,0.15)' : '0 4px 10px rgba(0,0,0,0.03)',
                      }
                    }}
                  >
                    {/* Lock Overlay for Unearned Badges */}
                    {!isEarned && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          color: 'text.disabled',
                          display: 'flex'
                        }}
                      >
                        <LockIcon fontSize="small" />
                      </Box>
                    )}

                    {/* Badge Icon */}
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: '50%',
                        backgroundColor: isEarned ? 'rgba(255,255,255,0.15)' : 'action.disabledBackground',
                        color: isEarned ? '#fff' : 'text.disabled',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                      }}
                    >
                      {badgeMeta.icon}
                    </Box>

                    {/* Title */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5, color: isEarned ? '#fff' : 'text.primary' }}>
                      {badgeMeta.title}
                    </Typography>

                    {/* Description / Requirement */}
                    <Typography variant="caption" sx={{ opacity: isEarned ? 0.9 : 0.7, maxWidth: 160, display: 'block', mb: 1 }}>
                      {isEarned ? earnedBadge.description : badgeMeta.requirement}
                    </Typography>

                    {/* Earned Date */}
                    {isEarned && earnedBadge.earnedAt && (
                      <Typography variant="caption" sx={{ fontWeight: 'bold', mt: 1, backgroundColor: 'rgba(255,255,255,0.2)', px: 1, py: 0.2, borderRadius: 1 }}>
                        Earned: {new Date(earnedBadge.earnedAt).toLocaleDateString('en-GB')}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      {progress && progress.instructorId && (
        <Paper 
          sx={{ 
            p: 3, 
            mb: 4, 
            borderRadius: 3, 
            border: '1px solid #e0e0e0',
            boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
            backgroundColor: '#fff'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1a237e', display: 'flex', alignItems: 'center', gap: 1 }}>
            ⭐ {hasExistingReview ? 'Edit Your Review' : 'Rate Your Instructor'} ({progress.instructorName})
          </Typography>
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'medium' }}>
                Select Rating:
              </Typography>
              <Rating
                name="instructor-rating"
                value={reviewRating}
                onChange={(event, newValue) => {
                  if (newValue !== null) {
                    setReviewRating(newValue);
                  }
                }}
                size="large"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                fullWidth
                label="Add a review comment (optional)"
                variant="outlined"
                size="small"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="Share your learning experience..."
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isPassedPracticalTest}
                    onChange={(e) => setIsPassedPracticalTest(e.target.checked)}
                    color="success"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                    🎉 I Passed My Test!
                  </Typography>
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleSubmitReview}
                disabled={submittingReview}
                sx={{ fontWeight: 'bold', borderRadius: 2 }}
              >
                {submittingReview ? 'Submitting...' : hasExistingReview ? 'Update Review' : 'Submit'}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}
      
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleChange} aria-label="student dashboard tabs" variant="fullWidth">
            <Tab label="Upcoming Lessons" icon={<ScheduleIcon />} iconPosition="start" {...a11yProps(0)} />
            <Tab label="Lesson History" icon={<CheckCircleIcon />} iconPosition="start" {...a11yProps(1)} />
            <Tab label="Book New Lesson" icon={<CalendarIcon />} iconPosition="start" {...a11yProps(2)} />
            <Tab label="Requests" icon={<MessageIcon />} iconPosition="start" {...a11yProps(3)} />
            <Tab label="Referral & Feedback" icon={<ShareIcon />} iconPosition="start" {...a11yProps(4)} />
            <Tab label="Test Routes" icon={<MapIcon />} iconPosition="start" {...a11yProps(5)} />
          </Tabs>
        </Box>
        
        <TabPanel value={value} index={0}>
          {/* Upcoming Lessons Tab */}
          {upcomingLessons.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, backgroundColor: '#fcfcfc' }}>
              <Typography color="textSecondary">You have no upcoming or pending scheduled lessons.</Typography>
            </Paper>
          ) : (
            <Box>
              <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
                <strong>Cancellation Policy:</strong> Booked lessons must be cancelled at least 24 hours in advance to avoid a late cancellation fee (100% of the lesson price).
              </Alert>
              {upcomingLessons.map((lesson) => {
                const isLateCancellationWindow = lesson.isApproved && (parseISO(lesson.lessonDate).getTime() - new Date().getTime()) / (1000 * 60 * 60) < 24;
                return (
                  <Paper key={lesson.id} sx={{ mb: 2, p: 3, borderRadius: 3, border: isLateCancellationWindow ? '1px solid #d32f2f' : '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', backgroundColor: isLateCancellationWindow ? '#fffbfa' : '#ffffff' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: isLateCancellationWindow ? '#d32f2f' : '#1a237e' }}>
                        {format(parseISO(lesson.lessonDate), 'dd MMMM yyyy - HH:mm')}
                      </Typography>
                      <Box display="flex" gap={1} alignItems="center">
                        {isLateCancellationWindow && (
                          <Chip 
                            label="Within 24h Window" 
                            color="error" 
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 'bold' }}
                          />
                        )}
                        <Chip 
                          label={lesson.isApproved ? "Approved" : "Pending Approval"} 
                          color={lesson.isApproved ? "success" : "warning"}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </Box>
                    </Box>
                    <Box mt={1.5} display="flex" flexDirection="column" gap={0.5}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Duration:</strong> {lesson.duration} minutes
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Instructor:</strong> {lesson.instructorName}
                      </Typography>
                      {lesson.topicName && (
                        <Box mt={0.5} display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" color="textSecondary"><strong>Topic:</strong></Typography>
                          <Chip 
                            label={`${lesson.topicCode || ''} - ${lesson.topicName}`} 
                            size="small" 
                            color="primary" 
                            variant="outlined" 
                            sx={{ fontWeight: '600' }}
                          />
                        </Box>
                      )}
                      {lesson.notes && (
                        <Typography variant="body2" color="textSecondary">
                          <strong>Notes:</strong> {lesson.notes}
                        </Typography>
                      )}
                      {isLateCancellationWindow && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          ⚠️ Cancelling this lesson now will incur a 100% late cancellation fee.
                        </Typography>
                      )}
                    </Box>
                    <Box mt={2} display="flex" justifyContent="flex-end">
                      <Button 
                        variant="outlined" 
                        color="error" 
                        size="small"
                        onClick={() => handleCancelLesson(lesson.id)}
                        sx={{ fontWeight: 'bold', borderRadius: 2 }}
                      >
                        Cancel / Delete Request
                      </Button>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}
        </TabPanel>
        
        <TabPanel value={value} index={1}>
          {/* Lesson History Tab */}
          {lessonHistory.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, backgroundColor: '#fcfcfc' }}>
              <Typography color="textSecondary">You have no lesson history.</Typography>
            </Paper>
          ) : (
            <Box>
              {lessonHistory.map((lesson) => (
                <Paper key={lesson.id} sx={{ mb: 2, p: 3, borderRadius: 3, border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {format(parseISO(lesson.lessonDate), 'dd MMMM yyyy')}
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Chip 
                        label={lesson.completed ? "Completed" : "Not Completed"} 
                        color={lesson.completed ? "success" : "default"}
                        variant={lesson.completed ? "filled" : "outlined"}
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                      {!lesson.isApproved && (
                        <Chip 
                          label="Rejected/Invalid" 
                          color="error"
                          variant="outlined"
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      )}
                    </Box>
                  </Box>
                  <Box mt={1.5} display="flex" flexDirection="column" gap={0.5}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Duration:</strong> {lesson.duration} minutes
                    </Typography>
                    {lesson.topicName && (
                      <Box mt={0.5} display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" color="textSecondary"><strong>Topic:</strong></Typography>
                        <Chip 
                          label={`${lesson.topicCode || ''} - ${lesson.topicName}`} 
                          size="small" 
                          color="primary" 
                          variant="outlined" 
                          sx={{ fontWeight: '600' }}
                        />
                      </Box>
                    )}
                    {lesson.notes && (
                      <Typography variant="body2" color="textSecondary">
                        <strong>Instructor Notes:</strong> {lesson.notes}
                      </Typography>
                    )}
                  </Box>
                  {lesson.topics && lesson.topics.length > 0 && (
                    <Box mt={2}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Topics Covered:</Typography>
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {lesson.topics.map((topic: any) => (
                          <Chip 
                            key={topic.id}
                            label={`${topic.topicName} ${topic.covered ? '✓' : '✗'}`}
                            color={topic.covered ? "primary" : "default"}
                            variant="outlined"
                            size="small"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Paper>
              ))}
            </Box>
          )}
        </TabPanel>
        
        <TabPanel value={value} index={2}>
          {/* Book a Lesson Tab */}
          {!progress?.instructorId ? (
            <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 8px 24px rgba(0,0,0,0.03)', textAlign: 'center' }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#d32f2f' }}>
                Booking Locked
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                You must be connected with an instructor to book lesson slots. Please browse our instructor directory to send a request. Once an instructor accepts your request, your booking capability will unlock automatically.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/search')}
                sx={{ fontWeight: 'bold', borderRadius: 2 }}
              >
                Search Instructors Directory
              </Button>
            </Paper>
          ) : (
            <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 8px 24px rgba(0,0,0,0.03)' }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, color: '#1a237e' }}>
                Your Instructor's Available Slots
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Select the date you want to book a lesson using the forward/backward arrows, then click on one of the available times below.
              </Typography>

              <Box display="flex" justifyContent="center" alignItems="center" gap={2} sx={{ mb: 4, mt: 2 }}>
                <IconButton 
                  onClick={() => {
                    const prev = new Date(selectedDate);
                    prev.setDate(prev.getDate() - 1);
                    setSelectedDate(prev);
                  }}
                  disabled={format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={selectedDate}
                    onChange={(date) => date && setSelectedDate(date)}
                    minDate={new Date()}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { width: 220 }
                      }
                    }}
                  />
                </LocalizationProvider>
                <IconButton 
                  onClick={() => {
                    const next = new Date(selectedDate);
                    next.setDate(next.getDate() + 1);
                    setSelectedDate(next);
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>

              {loadingSlots ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box>
                  {/* Booked lessons for selected date */}
                  {(() => {
                    const dayLessons = getLessonsForSelectedDate();
                    if (dayLessons.length === 0) return null;
                    return (
                      <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: '#2e7d32', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon color="success" /> Your Reservations for Today
                        </Typography>
                        <Grid container spacing={2}>
                          {dayLessons.map((lesson) => (
                            <Grid size={{ xs: 12 }} key={lesson.id}>
                              <Paper 
                                sx={{ 
                                  p: 2.5, 
                                  borderRadius: 3, 
                                  border: '2px solid #2e7d32', 
                                  backgroundColor: '#f1f8e9',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  flexWrap: 'wrap',
                                  gap: 2
                                }}
                              >
                                <Box>
                                  <Typography variant="h6" fontWeight="bold" color="#2e7d32">
                                    {format(parseISO(lesson.lessonDate), 'HH:mm')} - {format(new Date(parseISO(lesson.lessonDate).getTime() + lesson.duration * 60000), 'HH:mm')}
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                    <strong>Status:</strong> {lesson.isApproved ? "Approved" : "Pending Approval"} | <strong>Instructor:</strong> {lesson.instructorName}
                                  </Typography>
                                  {lesson.notes && (
                                    <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                      <strong>Your Notes:</strong> {lesson.notes}
                                    </Typography>
                                  )}
                                  {lesson.isApproved && (parseISO(lesson.lessonDate).getTime() - new Date().getTime()) / (1000 * 60 * 60) < 24 && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, fontWeight: 'bold', display: 'block' }}>
                                      ⚠️ Late Cancellation Fee Applies (less than 24h)
                                    </Typography>
                                  )}
                                </Box>
                                <Button 
                                  variant="contained" 
                                  color="error" 
                                  size="small"
                                  onClick={() => handleCancelLesson(lesson.id)}
                                  sx={{ fontWeight: 'bold', borderRadius: 2 }}
                                >
                                  Cancel / Delete Request
                                </Button>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                        <Divider sx={{ my: 4 }} />
                      </Box>
                    );
                  })()}

                  {availableSlots.length === 0 ? (
                    <Alert severity="info" sx={{ py: 2, borderRadius: 2 }}>
                      No available slots found for the selected date ({format(selectedDate, 'dd MMMM yyyy')}). Please check other days.
                    </Alert>
                  ) : (
                    <Grid container spacing={2}>
                      {availableSlots.map((slot) => {
                        const start = new Date(slot.startTime);
                        const end = new Date(slot.endTime);
                        return (
                          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={slot.id}>
                            <Paper 
                              onClick={() => handleSelectSlot(slot)}
                              sx={{ 
                                p: 3, 
                                textAlign: 'center', 
                                cursor: 'pointer',
                                borderRadius: 3,
                                border: '1px solid #e0e0e0',
                                backgroundColor: '#fff',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: '#1976d2',
                                  backgroundColor: 'rgba(25, 118, 210, 0.02)',
                                  boxShadow: '0 6px 18px rgba(0,0,0,0.06)'
                                }
                              }}
                            >
                              <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                                {format(start, 'HH:mm')} - {format(end, 'HH:mm')}
                              </Typography>
                              <Chip 
                                label={`${slot.duration || 60} Mins`} 
                                size="small" 
                                color="primary" 
                                variant="outlined" 
                                sx={{ fontWeight: 'bold' }}
                              />
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </Box>
              )}
            </Paper>
          )}
        </TabPanel>

        <TabPanel value={value} index={3}>
          {quotes.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, backgroundColor: '#fcfcfc' }}>
              <Typography color="textSecondary">You have not submitted any requests yet.</Typography>
            </Paper>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {quotes.map((quote) => (
                <Paper key={quote.id} sx={{ p: 3, borderRadius: 3, border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                      Instructor: {quote.instructorName}
                    </Typography>
                    <Chip 
                      label={quote.status} 
                      color={quote.status === 'Accepted' ? 'success' : quote.status === 'Rejected' ? 'error' : 'warning'}
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>
                  <Box mt={1.5} display="flex" flexDirection="column" gap={0.5}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Submitted:</strong> {format(parseISO(quote.createdAt), 'dd MMMM yyyy - HH:mm')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Preferred Transmission:</strong> {quote.preferredTransmission}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Experience Level:</strong> {quote.experienceLevel}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Your Message:</strong> "{quote.message}"
                    </Typography>
                    {quote.responseMessage && (
                      <Box mt={2} p={2} sx={{ bgcolor: 'rgba(25, 118, 210, 0.04)', borderRadius: 2, borderLeft: '4px solid #1976d2' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 0.5 }}>
                          Instructor's Response:
                        </Typography>
                        <Typography variant="body2" color="textPrimary" sx={{ fontStyle: 'italic' }}>
                          "{quote.responseMessage}"
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </TabPanel>

        <TabPanel value={value} index={4}>
          <ReferralAndReviews />
        </TabPanel>

        <TabPanel value={value} index={5}>
          <TestRoutesTab />
        </TabPanel>
      </Box>
    </Container>
  );
};

export default StudentDashboard;
